import { barrelRollTicks } from '@runkr/config'
import type { HazardBalance } from '@runkr/config'
import type { SegmentOf } from '@runkr/config'
import { cosDeg, sinDeg } from '../math/trig'
import type { Aabb, HazardPhase } from '../track/types'

/** One hazard's per-cycle-tick tables, indexed by `(tick + phaseTicks) % periodTicks`. */
export interface HazardTables {
  readonly phases: HazardPhase[]
  readonly boxes: Aabb[]
}

/** Physics the falling block shares with players. */
export interface FallPhysics {
  readonly gravityPxPerTick2: number
  readonly maxFallSpeedPxPerTick: number
}

/**
 * Pendulum: angle `θ = amplitude · sin(360° · k / period)` from straight down, tip at
 * `pivot + length · (sin θ, cos θ)`, hit box a square of `tipSizePx` centred on the tip.
 */
export function bladeTables(
  seg: SegmentOf<'swinging_blade' | 'log_ram'>,
  tipSizePx: number,
): HazardTables {
  const phases: HazardPhase[] = []
  const boxes: Aabb[] = []
  const half = tipSizePx / 2
  for (let k = 0; k < seg.periodTicks; k++) {
    const theta = seg.amplitudeDeg * sinDeg((360 * k) / seg.periodTicks)
    const tipX = seg.x + seg.lengthPx * sinDeg(theta)
    const tipY = seg.y + seg.lengthPx * cosDeg(theta)
    phases.push('swinging')
    boxes.push({ x: tipX - half, y: tipY - half, w: tipSizePx, h: tipSizePx })
  }
  return { phases, boxes }
}

/**
 * Cycle: `retracted`, then `telegraph` for the configured window, then `extended` for
 * `activeTicks` at the end of the period. Extended spikes are `spikeHeightPx` tall above `y`.
 */
export function spikeTables(seg: SegmentOf<'spike_strip'>, hz: HazardBalance): HazardTables {
  const extendAt = seg.periodTicks - seg.activeTicks
  const telegraphAt = extendAt - hz.spikeTelegraphTicks
  if (telegraphAt < 0) throw new Error('spike strip: period shorter than telegraph + active')
  const box: Aabb = { x: seg.x, y: seg.y - hz.spikeHeightPx, w: seg.w, h: hz.spikeHeightPx }
  const phases: HazardPhase[] = []
  const boxes: Aabb[] = []
  for (let k = 0; k < seg.periodTicks; k++) {
    phases.push(k < telegraphAt ? 'retracted' : k < extendAt ? 'telegraph' : 'extended')
    boxes.push(box)
  }
  return { phases, boxes }
}

/**
 * Trapdoor (Sky Meadow): `retracted` (closed), `telegraph` (rattling) for the configured
 * window, then `extended` (sprung stakes `trapdoorStakeHeightPx` tall above `y`) for
 * `trapdoorSprungTicks` at the end of the period.
 */
export function trapdoorTables(seg: SegmentOf<'trapdoor'>, hz: HazardBalance): HazardTables {
  const springAt = seg.periodTicks - hz.trapdoorSprungTicks
  const telegraphAt = springAt - hz.trapdoorTelegraphTicks
  if (telegraphAt < 0) throw new Error('trapdoor: period shorter than telegraph + sprung')
  const h = hz.trapdoorStakeHeightPx
  const box: Aabb = { x: seg.x, y: seg.y - h, w: seg.w, h }
  const phases: HazardPhase[] = []
  const boxes: Aabb[] = []
  for (let k = 0; k < seg.periodTicks; k++) {
    phases.push(k < telegraphAt ? 'retracted' : k < springAt ? 'telegraph' : 'extended')
    boxes.push(box)
  }
  return { phases, boxes }
}

/**
 * Barrel roll (Driftroot Lagoon, MAP_02 §2): `waiting`, then `telegraph` (the gate at `x + w`
 * rattles) for the configured window, then `rolling`: a `barrelSizePx` square on `y` whose right
 * edge starts at `x + w` and moves left `barrelSpeedPxPerTick` per tick until it reaches `x`. The
 * roll ends the period. Outside `rolling` the box rests behind the gate (never harmful).
 */
export function barrelTables(seg: SegmentOf<'barrel_roll'>, hz: HazardBalance): HazardTables {
  const roll = barrelRollTicks(seg.w, hz)
  const rollAt = seg.periodTicks - roll
  const telegraphAt = rollAt - hz.barrelTelegraphTicks
  if (telegraphAt < 0) throw new Error('barrel roll: period shorter than telegraph + roll')
  const s = hz.barrelSizePx
  const right = seg.x + seg.w
  const phases: HazardPhase[] = []
  const boxes: Aabb[] = []
  for (let k = 0; k < seg.periodTicks; k++) {
    const r = k - rollAt
    phases.push(k < telegraphAt ? 'waiting' : r < 0 ? 'telegraph' : 'rolling')
    const x = right - s - Math.max(0, r) * hz.barrelSpeedPxPerTick
    boxes.push({ x, y: seg.y - s, w: s, h: s })
  }
  return { phases, boxes }
}

/** A static hazard: one `armed` entry, so it hits whenever it is touched. */
export function staticTables(box: Aabb): HazardTables {
  return { phases: ['armed'], boxes: [box] }
}

/**
 * Block top offsets while falling from rest, one per tick until it has dropped `dropPx`:
 * same integration as players (average of old and new speed, capped fall speed).
 */
export function fallOffsets(dropPx: number, phys: FallPhysics): number[] {
  const out: number[] = []
  let vy = 0
  let d = 0
  while (d < dropPx) {
    const vy1 = Math.min(vy + phys.gravityPxPerTick2, phys.maxFallSpeedPxPerTick)
    d += (vy + vy1) / 2
    vy = vy1
    out.push(Math.min(d, dropPx))
  }
  return out
}

/**
 * Cycle: `waiting` up top, `telegraph` (shadow on the ground), `falling`, `resting` at the
 * bottom for the configured window, then it resets to the top for the next period.
 */
export function fallingBlockTables(
  seg: SegmentOf<'falling_block' | 'boulder_drop'>,
  hz: HazardBalance,
  phys: FallPhysics,
): HazardTables {
  const fall = fallOffsets(seg.dropPx, phys)
  const telegraphAt =
    seg.periodTicks - hz.fallingBlockRestTicks - fall.length - hz.fallingBlockTelegraphTicks
  if (telegraphAt < 0) throw new Error('falling block: period shorter than telegraph + fall + rest')
  const fallAt = telegraphAt + hz.fallingBlockTelegraphTicks
  const restAt = fallAt + fall.length
  const phases: HazardPhase[] = []
  const boxes: Aabb[] = []
  for (let k = 0; k < seg.periodTicks; k++) {
    const dy = k < fallAt ? 0 : k < restAt ? fall[k - fallAt]! : seg.dropPx
    phases.push(
      k < telegraphAt ? 'waiting' : k < fallAt ? 'telegraph' : k < restAt ? 'falling' : 'resting',
    )
    boxes.push({ x: seg.x, y: seg.y + dy, w: seg.w, h: seg.h })
  }
  return { phases, boxes }
}

/** Smallest box containing every box of the cycle. */
export function boundsOf(boxes: readonly Aabb[]): Aabb {
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const b of boxes) {
    x0 = Math.min(x0, b.x)
    y0 = Math.min(y0, b.y)
    x1 = Math.max(x1, b.x + b.w)
    y1 = Math.max(y1, b.y + b.h)
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}
